/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { createContext, useState, type ReactNode, useEffect, use } from 'react';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';
import { getDecodedCookiesSync, setCookie } from '@/utils/cookie';
import { useAppData } from '@/hooks/useAppData';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { routePaths } from '@/constants/routePaths';
import { useFeatureFlag } from '@/features/globalConfig';

type Banner90DayTypeProvider = {
  setOpenBannerFn: (open: boolean, enableSetCookie?: boolean) => void;
  openBanner: boolean;
};

const Banner90DayContext = createContext<Banner90DayTypeProvider>({} as Banner90DayTypeProvider);

export const useBanner90DayContext = () => {
  const context = use(Banner90DayContext);
  if (context === undefined) {
    throw new Error('useBanner90DayContext must be used within a Banner90DayProvider');
  }
  return context;
};

type Banner90DayProviderProps = {
  children: ReactNode;
};

export const Banner90DayProvider = ({ children }: Banner90DayProviderProps) => {
  const azrPilotChangeAlertType = useFeatureFlag('AZR_PILOT_CHANGE_ALERT_TYPE');
  const { appData } = useAppData();
  const { userAuthenticated } = appData;
  const router = useLegacyRouter();
  const isClient = typeof document !== 'undefined';
  const isCartPage = router.asPath.includes(routePaths.cart);
  const isCheckoutPage = router.asPath.includes(routePaths.checkout);
  const { data: myProfile } = useMyProfile();
  // Alert type "TC" is for 0 day
  // Alert type "90" is the 90 day banner
  const loyaltyAccountAlertPromptObject = myProfile?.loyaltyAccountAlerts?.find(
    (alert) => alert.alertType === azrPilotChangeAlertType,
  );
  const isPrompt = loyaltyAccountAlertPromptObject?.status === 'PROMPT';
  const pilotAlertSeenCookie = `${azrPilotChangeAlertType}PilotAlertSeen`;
  const cookies = isClient
    ? getDecodedCookiesSync([pilotAlertSeenCookie as '90DayBannerSeen'])
    : null;
  const isBannerViewed = isClient ? cookies?.get(pilotAlertSeenCookie as '90DayBannerSeen') : true;
  useEffect(() => {
    setOpenBanner(
      userAuthenticated && isPrompt && !isBannerViewed && !isCartPage && !isCheckoutPage,
    );
  }, [isPrompt, isBannerViewed, userAuthenticated, isCartPage, isCheckoutPage]);

  const [openBanner, setOpenBanner] = useState(isPrompt && !isCartPage && !isCheckoutPage);

  const setOpenBannerFn = (open: boolean, enableSetCookie = false) => {
    setOpenBanner(open);
    // for use cases where we only close the banner without setting the cookie
    if (enableSetCookie) {
      void setCookie(pilotAlertSeenCookie as '90DayBannerSeen', 'true');
    }
  };

  return (
    <Banner90DayContext
      value={{
        setOpenBannerFn,
        openBanner,
      }}
    >
      {children}
    </Banner90DayContext>
  );
};
